/**
 * @file _app.tsx
 *
 * @fileoverview This is the top level of the application. In the app page data and props
 * can be passed generically to any page, so if certain data (like the current user)
 * is needed on every page it can be added here
 * and will be made available automatically as props on any page.
 */

import { ApolloProvider } from '@apollo/client';
import { Session } from 'next-auth';
import { getSession, SessionProvider } from 'next-auth/react';
import App, { AppContext } from 'next/app';
import Router from 'next/router';
import NProgress from 'nprogress';
import React, { useEffect, useMemo } from 'react';
import { IntlProvider } from 'react-intl';
import { useApollo } from '~/jobiqo/apollo/initApollo';
import { JobiqoApp } from '~/jobiqo/components/App';
import MetatagDefault from '~/jobiqo/components/Metatag/MetatagDefault';
import { PathContext } from '~/jobiqo/context/app';
import UserProvider from '~/jobiqo/context/user';
import { getUser, isAnonymousByHeaders } from '~/jobiqo/lib/auth';
import { User } from '~/jobiqo/models/user';
import { getIntlLocale, handleIntlError } from '~/jobiqo/utils/intl-util';
import { getOriginalLink, getTranslatedLink } from '~/jobiqo/utils/links';
import redirect from '../lib/redirect';
import { getAppConfig, setEnabledModules } from '../lib/config';
import { AppConfig } from '../models/config';
import { ConfigProvider } from '../context/config';
import { URLProvider } from '../context/url';
import {
  FrontendConfigProvider,
  FrontendConfigContextType,
} from '../context/frontendConfig';
import { getUniversalFrontendConfig } from '../lib/frontendConfig';
import { NextFontSlot } from '~/jobiqo/components/Slots/NextFontSlot';
import SourceTracking from '~/jobiqo/components/SourceTracking';
import JobAlertActivityTracking from '~/jobiqo/components/JobAlert/ActivityTracking';
import { analyticsDispatchDelayed, EVENTS } from '../lib/analytics';
import { getUserDimensions } from '../components/User/utils/user_analytics';

// Messages cache so that we don't repeat fetching messages.
let messagesCache = {};

Router.events.on('routeChangeStart', (url) => {
  NProgress.start();
  window['__jq_routing'] = true;
});
Router.events.on('routeChangeComplete', () => {
  NProgress.done();
  window['__jq_routing'] = false;
});
Router.events.on('routeChangeError', () => NProgress.done());

interface IAppProps {
  /**
   * The current user.
   */
  user: User;
  /**
   * If the current user is authenticated.
   */
  authenticated: boolean;
  /**
   * The current session object.
   */
  session: any;
  /**
   * The component that is going to be rendered (page).
   */
  Component: any;
  /**
   * The initialized apollo client.
   */
  apolloClient: any;
  /**
   * The current locale.
   */
  locale: string;
  /**
   * The current region.
   */
  region: string;
  /**
   * The messages object from react-intl.
   */
  messages: any;
  /**
   * The current host.
   */
  host: string;
  /**
   * The current full path.
   *
   * If nothing is passed this would be the ctx.asPath from nextjs.
   */
  path: string;
  /**
   * Information passed by nextjs router.
   */
  router: any;
  /**
   * The  current page props.
   */
  pageProps: any;
  /**
   * Backend configuration.
   */
  backendConfig: AppConfig[];
  /**
   * Frontend configuration.
   */
  frontendConfig: FrontendConfigContextType;
}

const MyApp = ({
  apolloClient,
  authenticated,
  user,
  session,
  messages,
  host,
  path,
  pageProps,
  Component,
  router,
  backendConfig,
  frontendConfig,
}: IAppProps) => {
  // Sync enabled modules on the client before pages build their queries.
  setEnabledModules(backendConfig);

  const hostValue = useMemo(
    () =>
      host
        ? host
        : typeof window !== 'undefined'
        ? window?.location?.origin
        : '',
    [host],
  );

  const FontsWrapper = frontendConfig.APP.FONTS.OPTIMIZED
    ? NextFontSlot
    : React.Fragment;
  // We favour path from the page props. If that is not available we use _app.getInitialProps.
  const processedPath = pageProps.path ?? path;

  const locale = router.locale; // Here we actually get the language (for content).

  // Compute the original path before translation (e.g., /jobs instead of /stellenangebote).
  const originalPath = useMemo(() => getOriginalLink(processedPath, locale), [
    processedPath,
    locale,
  ]);
  const translatedPath = useMemo(
    () => getTranslatedLink(originalPath, locale),
    [originalPath, locale],
  );
  const dateFormats = getIntlLocale(router.locale, frontendConfig);

  // Apollo client info might come from the server via props.
  // But we get as fallback with client hooks.
  let apolloClientInialized = apolloClient;
  if (!apolloClientInialized) {
    apolloClientInialized = useApollo(pageProps, router.locale, session);
  }

  const masqueraded = Boolean(session?.masqueraded);

  useEffect(() => {
    // When logging in via social auth for the first time we pass a isNewUser flag,
    // if this is the case we want to track this. However this flag remains as
    // true for the entire nextAuth session so we need to ensure we only track this once.
    const signupEventDispatched = localStorage.getItem(
      'signup_event_dispatched',
    );

    if (session?.isNewUserViaSocial && signupEventDispatched !== 'true') {
      analyticsDispatchDelayed(EVENTS.SIGNUP_START, getUserDimensions(user));
      // SSO users are auto-verified, so they never reach the email verification
      // page where sign_up is normally triggered. Dispatch it here immediately.
      analyticsDispatchDelayed(EVENTS.SIGNUP_FINISH, getUserDimensions(user));
      localStorage.setItem('signup_event_dispatched', 'true');
    }

    // Clear the flag when the session ends.
    // This happens when a new session is started (the isNewUserViaSocial flag
    // is suddenly false or the user just logs out)
    return () => {
      if (!session?.isNewUserViaSocial) {
        localStorage.removeItem('signup_event_dispatched');
      }
    };
  }, [session?.isNewUserViaSocial]);

  return (
    <FontsWrapper>
      <SessionProvider
        session={session}
        {...(frontendConfig.APP.BASE_PATH
          ? { basePath: `${frontendConfig.APP.BASE_PATH}/api/auth` }
          : {})}
      >
        <IntlProvider
          locale={dateFormats}
          messages={messages}
          onError={handleIntlError}
        >
          <ApolloProvider client={apolloClientInialized}>
            <PathContext.Provider
              value={{
                querystring: router.query,
                pathname: router.pathname,
                path: processedPath,
                host: hostValue,
                originalPath,
                translatedPath,
              }}
            >
              <FrontendConfigProvider config={frontendConfig}>
                <ConfigProvider config={backendConfig}>
                  <URLProvider>
                    <UserProvider
                      user={user}
                      masqueraded={masqueraded}
                      authenticated={authenticated}
                    >
                      <MetatagDefault host={host} />
                      <JobiqoApp
                        {...{
                          apolloClient,
                          Component,
                          authenticated,
                          user,
                          path: processedPath,
                          host: host
                            ? host
                            : typeof window !== 'undefined'
                            ? window?.location?.origin
                            : '',
                          pageProps,
                          dateFormats,
                          locale,
                          masqueraded,
                        }}
                      />
                      <SourceTracking
                        router={router}
                        backendConfig={backendConfig}
                      />
                      <JobAlertActivityTracking />
                    </UserProvider>
                  </URLProvider>
                </ConfigProvider>
              </FrontendConfigProvider>
            </PathContext.Provider>
          </ApolloProvider>
        </IntlProvider>
      </SessionProvider>
    </FontsWrapper>
  );
};
MyApp.getInitialProps = async (appContext: AppContext) => {
  // Log user in if we are receiving POST one time login data.
  const appProps = await App.getInitialProps(appContext);

  // Load frontend configuration from config files.
  // Because this function runs both on the server on a full reload
  // and on the client during page transitions we need to ensure this function
  // can handle both cases. On the server we can load config directly from files,
  // but on the client we need to fetch it from the API route.
  const frontendConfig: FrontendConfigContextType = await getUniversalFrontendConfig(
    {
      ctx: appContext.ctx,
    },
  );
  const {
    APP: { BASE_PATH },
  } = frontendConfig;

  let currentPath = '';
  let host = process.env.CLIENT_URL;
  if (appContext.ctx.req) {
    currentPath = appContext.Component.getInitialProps
      ? (appContext.ctx.req.url as string)
      : (appContext.ctx.asPath as string);
  } else {
    currentPath = appContext.ctx.asPath as string;
    host = `${window.location.origin}${BASE_PATH ? `${BASE_PATH}` : ''}`;
  }

  // In order to avoid multiple calls repeated with the same result
  // this implements a very simple cache for the languages in the current language.
  if (
    appContext.ctx.locale &&
    (appContext.ctx.req || !messagesCache[appContext.ctx.locale ?? 0])
  ) {
    try {
      const messagesFromAPI = await fetch(
        `${host}/api/messages/${appContext.ctx.locale}`,
      );
      const messagesJson = await messagesFromAPI.json();
      messagesCache[appContext.ctx.locale ?? 0] = JSON.parse(
        messagesJson.messages,
      );
    } catch (e) {
      console.error('Failed to parse messages from api.', e);
    }
  }
  const messages = messagesCache?.[appContext.ctx.locale ?? 0] ?? {};

  // Try to read current config.
  const backendConfig = await getAppConfig(appContext.ctx);

  // @todo Refactor the Next.js page setup to not call getSession() during server side rendering.
  // next-auth doesn't support calling getServerSession from getInitialProps.
  // When we move to the new app dir this will be removed as we can rely on always being on the server to get this information.
  // https://github.com/nextauthjs/next-auth/discussions/4068
  let session: Session | null = null;
  if (!appContext.ctx.req || !isAnonymousByHeaders(appContext.ctx.req)) {
    session = await getSession(appContext.ctx);
  }

  const authenticated = !!session?.jwt;

  let user = {};
  try {
    // Log user in if we are receiving POST one time login data.
    // We are inside a try catch because we don't want the entire page to crash if this
    // query fails. It's still a good indication next queries might fail also, but because we are outside the
    // normal ApolloContext we have to not let unhandled errors go by.
    user = await getUser(appContext.ctx);
  } catch (e) {}

  // If the user is authenticated but the endpoint returns null
  // there is a mistmatch and we log the user out.
  // This works as a sanity check for the user. Should normally not happen but can happen
  // if a user is blocked or removed while "in session"
  if (authenticated && !user) {
    redirect('/user/logout', appContext.ctx);
    return;
  }

  // A session cookie without a backend jwt is a failed sign in, e.g. a denied
  // OAuth login. Drop the cookie and send the user back to login.
  if (!isAnonymousByHeaders(appContext.ctx.req) && !authenticated) {
    redirect('/user/logout', appContext.ctx, {
      type: 'danger',
      text:
        'Your sign in could not be completed. Please try again or contact support.',
    });
    return;
  }

  return {
    authenticated,
    user,
    session,
    messages,
    path: currentPath,
    host,
    ...appProps,
    backendConfig,
    frontendConfig,
  };
};

const OutputApp = MyApp;
export default OutputApp;
